import { useLocation, useNavigate } from 'react-router';
import { Icon } from '@/kit/Icon';
import { Tooltip } from '@/kit/Tooltip';
import { isItemActive, type SideMenuItem } from './SideMenu';
import './AdjustmentsReportsNav.scss';

interface SectionNavProps {
  label: string;
  items: SideMenuItem[];
  testId: string;
  /** Smaller configuration options at the right end of the bar (APT375: Dimensions inside Management Reports). */
  secondaryItems?: SideMenuItem[];
  /** A long bar (APT371: up to nine pages) wraps onto a second row instead of scrolling sideways, so no tab is ever cut off. */
  wrap?: boolean;
}

/**
 * The navigation bar across the top of the screens of a grouped menu section (APT348 Adjustments & Reports, APT353 Compliance & Filing,
 * APT370 Revised Accounts, APT371 Report Settings, APT375 Management Reports): the current screen is highlighted and a click opens another
 * one's existing route. Navigation only - none of the screens changes.
 */
export function SectionNav({ label, items, testId, secondaryItems, wrap = false }: SectionNavProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const button = (item: SideMenuItem, secondary = false) => {
    // A screen that is also reached by an older address (alsoActiveFor) keeps its tab highlighted there.
    const active = isItemActive(pathname, item);
    return (
      <button
        key={item.to}
        type="button"
        className={`adjustments-nav__item ${secondary ? 'adjustments-nav__item--secondary' : ''} ${active ? 'active' : ''}`
          .replace(/\s+/g, ' ')
          .trim()}
        aria-current={active ? 'page' : undefined}
        onClick={() => void navigate(item.to)}
      >
        <Icon name={item.icon} outlined />
        <span>{item.label}</span>
        {active && item.infoText && (
          <Tooltip title={item.infoText}>
            <Icon name="info" outlined className="adjustments-nav__info-icon" />
          </Tooltip>
        )}
      </button>
    );
  };

  return (
    <nav className={`adjustments-nav${wrap ? ' adjustments-nav--wrap' : ''}`} aria-label={label} data-testid={testId}>
      {items.map((item) => button(item))}
      {secondaryItems && secondaryItems.length > 0 && (
        <div className="adjustments-nav__secondary">{secondaryItems.map((item) => button(item, true))}</div>
      )}
    </nav>
  );
}
